import app from "./firebase";
import {
  getAuth,
  GoogleAuthProvider,
  signOut,
  signInWithPopup,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { useState, useEffect } from "react";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { Image } from "next/image";
import { userLogin } from "@/services/users";

const auth = getAuth(app);
const allowedDomains = ["music.ai", "moises.ai"];
const allowedEmails = (process.env.NEXT_PUBLIC_ALLOWED_EMAILS || "")
  .split(",")
  .map((e) => e.trim().toLowerCase())
  .filter(Boolean);

async function getUserRole(uid) {
  const firestore = getFirestore(app);
  const userDocRef = doc(firestore, "users", uid);
  const userDoc = await getDoc(userDocRef);

  if (userDoc.exists()) {
    const userData = userDoc.data();
    if (userData.role === "Admin") {
      return true;
    } else {
      logout();
      return false;
    }
  }
}

async function loginWithGoogle() {
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const userEmail = result.user.email.toLowerCase();
    const emailDomain = userEmail.substring(userEmail.lastIndexOf("@") + 1);
    if (
      !allowedDomains.includes(emailDomain) &&
      !allowedEmails.includes(userEmail)
    ) {
      await logout();
      throw new Error("Please use your music.ai or moises.ai email");
    }
    await userLogin();
    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function loginWithGoogleForDeletion() {
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    await userLogin();
    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

function registerWithEmail(email, password) {
  return createUserWithEmailAndPassword(auth, email, password)
    .then(async (userCredential) => {
      const user = userCredential.user;
      await userLogin();
      return user;
    })
    .catch((error) => {
      console.error(error);
      throw error;
    });
}

function loginWithEmail(email, password) {
  return signInWithEmailAndPassword(auth, email, password)
    .then(async (userCredential) => {
      const user = userCredential.user;
      await userLogin();
      return user;
    })
    .catch((error) => {
      console.error(error);
    });
}

function resetPassword(email) {
  return sendPasswordResetEmail(auth, email)
    .then(() => {
      console.log("Password reset email sent");
    })
    .catch((error) => {
      console.error(error);
    });
}

function logout() {
  return signOut(auth)
    .then(() => {})
    .catch((error) => {});
}

export {
  loginWithGoogle,
  loginWithGoogleForDeletion,
  logout,
  getUserRole,
  registerWithEmail,
  loginWithEmail,
  resetPassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
};

const UserProfile = () => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser({
          displayName: user.displayName,
          email: user.email,
          photoURL: user.photoURL,
          uid: user.uid,
        });
      } else {
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const renderLoginOrUserProfile = () => {
    if (user) {
      return (
        <div>
          <Image src={user.photoURL} alt={user.displayName}></Image>
          <p>{user.displayName}</p>
          <button onClick={logout}>Logout</button>
        </div>
      );
    } else {
      return (
        <div>
          <button onClick={loginWithGoogle}>Login with Google</button>
        </div>
      );
    }
  };

  return (
    <div>
      <h1>Authentication</h1>
      {renderLoginOrUserProfile()}
    </div>
  );
};

export default UserProfile;
